import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { 
  Firestore, 
  doc, 
  collection, 
  query, 
  where, 
  addDoc, 
  deleteDoc, 
  updateDoc,
  orderBy,
  collectionData,
  docData,
  limit,
  startAfter,
  getDocs,
  getCountFromServer
} from '@angular/fire/firestore';
import { Timestamp, QueryConstraint, serverTimestamp } from 'firebase/firestore';

export interface PaginatedResult<T> {
  items: T[];
  lastDoc: any | null;
  hasMore: boolean;
  totalCount?: number;
}
import { Storage, ref, uploadString, deleteObject, getDownloadURL, listAll } from "@angular/fire/storage";
import { Auth } from '@angular/fire/auth';
import { NewItem } from '../models/new-item';
import { SearchFilter } from '../models/search-filter';
import { NewEvent } from '../models/new-event';
import { Observable, from, of, firstValueFrom } from 'rxjs';
import { switchMap, catchError, map } from 'rxjs/operators';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class ItemsService {
  private readonly apiBase = environment.apiURL + '/api/v1';
  private readonly url = environment.apiURL + '/api/v1/items';
  private readonly firestore: Firestore = inject(Firestore);
  private readonly auth: Auth = inject(Auth);
  private readonly storage: Storage = inject(Storage);
  private readonly authService: AuthService = inject(AuthService);
  public async getDealerUidsByName(queryName: string): Promise<{ uids: string[]; hasMarketplaceItems?: boolean; hasCollectionItems?: boolean }> {
    if (!queryName || queryName.trim().length === 0) return { uids: [] };
    const q = queryName.trim();
    try {
      const res = await firstValueFrom(
        this.httpClient.get<{ uids: string[]; hasMarketplaceItems?: boolean; hasCollectionItems?: boolean }>(
          `${this.apiBase}/dealers/lookup?q=${encodeURIComponent(q)}`
        )
      );
      return res || { uids: [] };
    } catch (err) {
      console.warn("Could not lookup dealer uids via API:", err);
      return { uids: [] };
    }
  }

  constructor(private httpClient: HttpClient) {}

  public bestDeals(_category: string = 'all', limitCount: number = 12): Observable<NewItem[]> {
    const itemsCollection = collection(this.firestore, 'items');
    let constraints: QueryConstraint[] = [where('visible', '==', true), where('selling', '==', true)];
    if (_category !== 'all') {
      constraints.push(where('category', '==', _category));
    }
    if (limitCount > 0) {
      constraints.push(limit(limitCount));
    }
    const q = query(itemsCollection, ...constraints);
    return (collectionData(q, { idField: 'id' }) as Observable<NewItem[]>).pipe(
      map(items => items.filter(item => !(item as any).isPlaceholder || (item as any).showcaseInMarketplace))
    );
  }

  public newCollection(_category: string = 'all', limitCount: number = 12): Observable<NewItem[]> {
    const itemsCollection = collection(this.firestore, 'items');
    let constraints: QueryConstraint[] = [where('visible', '==', true), where('selling', '==', false)];
    if (_category !== 'all') {
      constraints.push(where('category', '==', _category));
    }
    if (limitCount > 0) {
      constraints.push(limit(limitCount));
    }
    const q = query(itemsCollection, ...constraints);
    return (collectionData(q, { idField: 'id' }) as Observable<NewItem[]>).pipe(
      map(items => items.filter(item => !(item as any).isPlaceholder || (item as any).showcaseInMarketplace))
    );
  }

  public async createItemFirebase(itemForm: NewItem): Promise<string> {
    // 1. Resolve user ID from Firebase Auth or persistent cached session
    let user = (this.auth as any).currentUser || this.authService.getAuthInstance().currentUser;
    if (!user) {
      await this.authService.waitForAuthReady();
      user = (this.auth as any).currentUser || this.authService.getAuthInstance().currentUser;
    }

    const userId = user?.uid || this.authService.userData?.uid;
    if (!userId) {
      throw new Error("Your session could not be verified. Please sign in to catalog this piece.");
    }

    // Deep sanitize object to strip any `undefined` values that break Firestore
    const rawData = JSON.parse(JSON.stringify(itemForm));
    const sanitizedDocData: any = {
      ...rawData,
      user_id: userId,
      likes: 0,
      created: new Date(),
      last_update: new Date(),
    };

    // Ensure no undefined keys remain
    for (const [key, value] of Object.entries(sanitizedDocData)) {
      if (value === undefined) {
        delete sanitizedDocData[key];
      }
    }

    // Try client Firestore addDoc if active user is attached to client
    if (user) {
      try {
        const docRef = await addDoc(collection(this.firestore, 'items'), sanitizedDocData);
        return docRef.id;
      } catch (clientErr: any) {
        console.warn("Client addDoc failed, falling back to backend Admin SDK:", clientErr);
      }
    }

    // Fall back to backend Admin SDK with active/renewed token from AuthService/localStorage
    const token = await this.authService.getValidToken();
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const res: any = await firstValueFrom(
      this.httpClient.post(`${this.url}`, sanitizedDocData, { headers })
    );
    const newId = res?.id || res?.items;
    if (!newId) {
      throw new Error("Failed to record item in registry. Please check your connection.");
    }
    return newId;
  }
  
  public async removeItem(item: NewItem): Promise<void> {
    if (!item.id) {
      throw new Error("Item ID is missing.");
    }
    // Attempt to remove images from storage, but ignore missing file errors
    if (item.photos && item.photos.length > 0) {
      try {
        await this.removeItemImages(item.id, item.photos);
      } catch (err) {
        console.warn("Storage images removal warning:", err);
      }
    }
    try {
      const docRef = doc(this.firestore, `items/${item.id}`);
      await deleteDoc(docRef);
    } catch (clientErr) {
      console.warn("Client deleteDoc failed, falling back to backend Admin SDK:", clientErr);
      const token = await this.authService.getValidToken();
      if (token) {
        const headers = { 'Authorization': `Bearer ${token}` };
        await firstValueFrom(this.httpClient.post(`${this.url}/${item.id}/delete`, {}, { headers }));
      } else {
        throw clientErr;
      }
    }
  }


  public async uploadItemImages(itemId: string, images: string[]): Promise<string[]> {
    if (!images || images.length === 0) return [];

    // 1. Primary: Upload via Backend Admin SDK directly into Cloud Storage (bypasses browser client auth restrictions)
    try {
      const token = await this.authService.getValidToken();
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const res: any = await firstValueFrom(
        this.httpClient.post(`${this.url}/${itemId}/photos`, { images }, { headers })
      );
      if (res && res.urls && res.urls.length > 0) {
        return res.urls;
      }
    } catch (backendErr) {
      console.warn("Backend photo upload failed, attempting client direct upload:", backendErr);
    }

    // 2. Client fallback via Firebase Storage SDK
    let user = (this.auth as any).currentUser || this.authService.getAuthInstance().currentUser;
    if (!user) {
      await this.authService.waitForAuthReady();
      user = (this.auth as any).currentUser || this.authService.getAuthInstance().currentUser;
    }
    const userId = user?.uid || this.authService.userData?.uid;
    if (!userId) {
      console.warn("No user ID found for uploadItemImages, skipping client upload.");
      return [];
    }
    const uploadedUrls: string[] = [];
    await Promise.all(images.map((image, index) => {
      if (image.startsWith('http://') || image.startsWith('https://')) {
        uploadedUrls.push(image);
        return Promise.resolve();
      }
      const storageRef = ref(this.storage, `items/${userId}/${itemId}/${index}`);
      return uploadString(storageRef, image, 'data_url').then(async (snap: any) => {
        try {
          const dl = await getDownloadURL(snap.ref);
          uploadedUrls.push(dl);
        } catch {
          uploadedUrls.push(`https://storage.googleapis.com/artiquary.appspot.com/items/${userId}/${itemId}/${index}`);
        }
      }).catch((err: any) => {
        console.warn(`Storage upload image ${index} notice:`, err);
      });
    }));
    return uploadedUrls;
  }

  public removeItemImages(itemId: string, images: (string | number)[]): Promise<any[]> {
    const user = (this.auth as any).currentUser;
    if (!user) {
      return Promise.resolve([]);
    }
    return Promise.all(images.map((_, index) => {
      const storageRef = ref(this.storage, `items/${user.uid}/${itemId}/${index}`);
      return deleteObject(storageRef).catch((err: any) => {
        console.warn(`Could not delete storage image ${index} (continuing):`, err);
        return null;
      });
    }));
  }

  /**
   * Sets both marketplace status (selling) and public/private visibility (visible).
   * Tries client Firestore updateDoc first, falling back to backend Admin SDK endpoint.
   */
  public async setItemVisibility(itemId: string, selling: boolean, visible: boolean): Promise<any> {
    try {
      const itemRef = doc(this.firestore, `items/${itemId}`);
      await updateDoc(itemRef, {
        selling,
        visible,
        last_update: Timestamp.fromDate(new Date())
      });
      return { success: true, selling, visible };
    } catch (clientErr) {
      console.warn("Client updateDoc for visibility failed, falling back to backend Admin SDK:", clientErr);
    }

    const token = await this.authService.getValidToken();
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return await firstValueFrom(
      this.httpClient.post(`${this.url}/${itemId}/visibility`, { selling, visible }, { headers })
    );
  }

  /**
   * Hides an item from marketplace listings and public search rather than permanently deleting.
   * Preserves unbroken provenance lineage and historical milestones in owner's collection.
   */
  public async hideItem(itemId: string, hideCompletely: boolean = true): Promise<void> {
    if (hideCompletely) {
      await this.setItemVisibility(itemId, false, false);
    } else {
      await this.setItemVisibility(itemId, false, true);
    }
  }

  /**
   * Restores an item's visibility in search and collection.
   */
  public async unhideItem(itemId: string): Promise<void> {
    await this.setItemVisibility(itemId, false, true);
  }


  public getImages(itemId: string, claimToken?: string): Observable<string[]> {
    return this.getItem(itemId, claimToken).pipe(
      switchMap(itemData => {
        if (!itemData) {
          return of([]);
        }
        if ((itemData as any).photoUrls && (itemData as any).photoUrls.length > 0) {
          return of((itemData as any).photoUrls);
        }
        // Fallback to backend API GET /api/v1/items/:id/photos
        const claimParam = claimToken ? `?claimToken=${encodeURIComponent(claimToken)}` : '';
        return this.httpClient.get<string[]>(`${this.url}/${itemId}/photos${claimParam}`).pipe(
          catchError(() => {
            const ownerId = itemData['user_id'];
            const photos = (itemData.photos && itemData.photos.length > 0) ? itemData.photos : [0];
            const urls = photos.map((photo: any) => `https://storage.googleapis.com/artiquary.appspot.com/items/${ownerId}/${itemId}/${photo}`);
            return of(urls);
          })
        );
      })
    );
  }

  public getThumbnail(itemId: string, claimToken?: string): Observable<string> {
    return this.getItem(itemId, claimToken).pipe(
      switchMap(itemData => {
        if (!itemData || !itemData['user_id']) {
          return of('');
        }
        if ((itemData as any).photoUrls && (itemData as any).photoUrls.length > 0) {
          return of((itemData as any).photoUrls[0]);
        }
        const ownerId = itemData['user_id'];
        const photoIndex = (itemData.photos && itemData.photos.length > 0) ? itemData.photos[0] : 0;
        return of(`https://storage.googleapis.com/artiquary.appspot.com/items/${ownerId}/${itemId}/${photoIndex}`);
      })
    );
  }  

  public async getItemsPaginated(
    title: string = '',
    filterForm: SearchFilter = { selling: true, category: 'all', condition: 'all' },
    pageSize: number = 24,
    lastDocSnapshot: any = null,
    fetchCount: boolean = false
  ): Promise<PaginatedResult<NewItem>> {
    let constraints: QueryConstraint[] = [where('visible', '==', true)];
    const trimmedTitle = title ? title.trim().toLowerCase() : '';
    if (trimmedTitle !== '') {
      const words = trimmedTitle.split(/\s+/).filter(w => w.length > 0).slice(0, 10);
      if (words.length > 0) {
        constraints.push(where('titleList', 'array-contains-any', words));
      }
    }
    if (filterForm.category && filterForm.category !== 'all') {
      constraints.push(where('category', '==', filterForm.category));
    } 
    if (filterForm.condition && filterForm.condition !== 'all') {
      constraints.push(where('condition', '==', filterForm.condition));
    } 
    constraints.push(where('selling', '==', filterForm.selling));

    if (filterForm.dealer && filterForm.dealer.trim()) {
      const lookupResult = await this.getDealerUidsByName(filterForm.dealer);
      const dealerUids = lookupResult.uids;
      if (dealerUids.length === 0) {
        constraints.push(where('user_id', '==', '__no_matching_dealer__'));
      } else if (dealerUids.length === 1) {
        constraints.push(where('user_id', '==', dealerUids[0]));
      } else {
        constraints.push(where('user_id', 'in', dealerUids.slice(0, 30)));
      }
    }

    const baseColl = collection(this.firestore, 'items');
    let totalCount: number | undefined;

    if (fetchCount) {
      try {
        const countQuery = query(baseColl, ...constraints);
        const countSnap = await getCountFromServer(countQuery);
        totalCount = countSnap.data().count;
      } catch (countErr) {
        console.warn("Could not retrieve count from server:", countErr);
      }
    }

    let collectedItems: NewItem[] = [];
    let currentLastDoc = lastDocSnapshot;
    let hasMore = true;

    while (collectedItems.length < pageSize && hasMore) {
      const pageConstraints: QueryConstraint[] = [...constraints];
      if (currentLastDoc) {
        pageConstraints.push(startAfter(currentLastDoc));
      }
      const fetchBatchSize = Math.max(pageSize - collectedItems.length, 12);
      pageConstraints.push(limit(fetchBatchSize));

      const pageQuery = query(baseColl, ...pageConstraints);
      const snap = await getDocs(pageQuery);
      const docs = snap.docs;

      if (docs.length === 0) {
        hasMore = false;
        break;
      }

      currentLastDoc = docs[docs.length - 1];
      if (docs.length < fetchBatchSize) {
        hasMore = false;
      }

      const validItems = docs
        .map((d: any) => ({ id: d.id, ...d.data() } as NewItem))
        .filter((item: any) => !item.isPlaceholder || item.showcaseInMarketplace);

      collectedItems.push(...validItems);
    }

    return {
      items: collectedItems,
      lastDoc: currentLastDoc,
      hasMore,
      totalCount
    };
  }

  public itemsSearch(title: string, filterForm: SearchFilter): Observable<NewItem[]> {
    let constraints: QueryConstraint[] = [where('visible', '==', true)];
    const trimmedTitle = title ? title.trim().toLowerCase() : '';
    if (trimmedTitle !== '') {
      const words = trimmedTitle.split(/\s+/).filter(w => w.length > 0).slice(0, 10);
      if (words.length > 0) {
        constraints.push(where('titleList', 'array-contains-any', words));
      }
    }
    if (filterForm.category !== 'all') {
      constraints.push(where('category', '==', filterForm.category));
    } 
    if (filterForm.condition !== 'all') {
      constraints.push(where('condition', '==', filterForm.condition));
    } 
    constraints.push(where('selling', '==', filterForm.selling));
    constraints.push(limit(48));
    const q = query(collection(this.firestore, 'items'), ...constraints);
    return (collectionData(q, { idField: 'id' }) as Observable<NewItem[]>).pipe(
      map(items => items.filter(item => !(item as any).isPlaceholder || (item as any).showcaseInMarketplace))
    );
  }

  public getMyItems(_category: string = 'all'): Observable<NewItem[]> {
    const user = (this.auth as any).currentUser;
    const userId = user?.uid || this.authService.userData?.uid;
    if (!userId) {
      return of([]);
    }
    let constraints: QueryConstraint[] = [where('user_id', '==', userId)];
    if (_category !== 'all') {
      constraints.push(where('category', '==', _category));
    }
    const q = query(collection(this.firestore, 'items'), ...constraints);
    return collectionData(q, { idField: 'id' }) as Observable<NewItem[]>;
  }

  public getItem(itemId: string, claimToken?: string): Observable<NewItem | undefined> {
    const claimParam = claimToken ? `?claimToken=${encodeURIComponent(claimToken)}` : '';
    return this.httpClient.get<NewItem>(`${this.url}/${itemId}${claimParam}`).pipe(
      catchError(err => {
        console.warn("Backend getItem failed, falling back to client docData:", err);
        const docRef = doc(this.firestore, `items/${itemId}`);
        return (docData(docRef) as Observable<NewItem | undefined>).pipe(
          catchError(() => of(undefined))
        );
      })
    );
  }

  public async createEvent(itemId: string, eventForm: any): Promise<any> {
    const sanitized: any = {};
    for (const [key, value] of Object.entries(eventForm)) {
      if (value !== undefined) {
        sanitized[key] = value;
      }
    }
    if (!sanitized.date) {
      sanitized.date = new Date();
    }
    try {
      const itemRef = doc(this.firestore, `items/${itemId}`);
      return await addDoc(collection(itemRef, 'events'), sanitized);
    } catch (clientErr: any) {
      console.warn("Client addDoc for event failed, falling back to backend Admin SDK:", clientErr);
      const user = (this.auth as any).currentUser;
      if (user) {
        const token = await user.getIdToken();
        const payload: any = { ...sanitized };
        if (payload.date instanceof Date) {
          payload.date = payload.date.toISOString();
        }
        return await firstValueFrom(
          this.httpClient.post(`${this.url}/${itemId}/events`, payload, {
            headers: { Authorization: token }
          })
        );
      } else {
        throw clientErr;
      }
    }
  }

  public getEvents(itemId: string): Observable<NewEvent[]> {
    const eventsCollection = collection(doc(this.firestore, `items/${itemId}`), 'events');
    const q = query(eventsCollection, orderBy("date", "desc"));
    return collectionData(q, { idField: 'id' }) as Observable<NewEvent[]>;
  }

  public removeEvent(item: NewItem, eventId: string): Promise<void> {
    if (!eventId || !item.id) {
      return Promise.reject(new Error("Item or Event ID is missing."));
    }
    const eventRef = doc(this.firestore, `items/${item.id}/events/${eventId}`);
    return deleteDoc(eventRef);
  }

  /**
   * Continuous Provenance & Lifecycle Transfer:
   * Transfers ownership to a new buyer/collector without ending the object's lifecycle.
   * Records a 'newoner' event preserving historical continuity and adds the item to the buyer's collection.
   */
  public async transferOwnership(
    itemId: string,
    newOwnerId: string,
    newOwnerName: string,
    notes?: string
  ): Promise<void> {
    const itemRef = doc(this.firestore, `items/${itemId}`);
    await updateDoc(itemRef, {
      user_id: newOwnerId,
      selling: false,
      last_update: serverTimestamp()
    });

    const eventForm: Partial<NewEvent> = {
      type: 'newoner',
      title: 'Provenance & Custody Transfer',
      description: notes || `Acquisition completed. Ownership transferred to ${newOwnerName}. Continuous provenance and historical lineage verified and preserved.`,
      date: serverTimestamp() as any,
      author: newOwnerName,
      likes: 0
    };

    await addDoc(collection(itemRef, 'events'), eventForm);
  }

  /**
   * Toggles an item between collection exhibition and marketplace sale
   */
  public async updateItemSelling(itemId: string, selling: boolean, price?: number): Promise<void> {
    const itemRef = doc(this.firestore, `items/${itemId}`);
    const updateData: any = {
      selling,
      last_update: serverTimestamp()
    };
    if (price !== undefined) {
      updateData.price = price;
    }
    await updateDoc(itemRef, updateData);
  }

  /**
   * Increments item like count
   */
  public async likeItem(itemId: string, currentLikes: number = 0): Promise<void> {
    const itemRef = doc(this.firestore, `items/${itemId}`);
    await updateDoc(itemRef, {
      likes: (currentLikes || 0) + 1
    });
  }

  public async updateItemField(itemId: string, data: any): Promise<void> {
    // Sanitize data: remove undefined fields
    const sanitized: any = {};
    for (const [key, value] of Object.entries(data)) {
      if (value !== undefined) {
        sanitized[key] = value;
      }
    }
    if (!sanitized.last_update) {
      sanitized.last_update = new Date();
    }

    try {
      const itemRef = doc(this.firestore, `items/${itemId}`);
      await updateDoc(itemRef, sanitized);
      return;
    } catch (clientErr: any) {
      console.warn("Client updateDoc failed, falling back to backend Admin SDK:", clientErr);
    }

    const token = await this.authService.getValidToken();
    if (token) {
      const payload: any = {};
      for (const [key, value] of Object.entries(sanitized)) {
        if (key !== 'last_update' && typeof value !== 'function') {
          payload[key] = value;
        }
      }
      await firstValueFrom(
        this.httpClient.post(`${this.url}/${itemId}/update`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );
    } else {
      throw new Error("Authentication required to update item.");
    }
  }

  /**
   * Admin: List all placeholder and pre-seeded dealers.
   */
  public async adminGetDealers(): Promise<any[]> {
    const token = await this.authService.getValidToken();
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res: any = await firstValueFrom(
      this.httpClient.get(`${this.apiBase}/admin/dealers`, { headers })
    );
    return res?.dealers || [];
  }

  /**
   * Admin: Fetch all items belonging to a specific placeholder dealer.
   */
  public async adminGetDealerItems(dealerId: string): Promise<any[]> {
    const token = await this.authService.getValidToken();
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res: any = await firstValueFrom(
      this.httpClient.get(`${this.apiBase}/admin/dealers/${dealerId}/items`, { headers })
    );
    return res?.items || [];
  }

  /**
   * Admin: Delete placeholder dealer and clean up seeded items.
   */
  public async adminDeleteDealer(dealerId: string): Promise<any> {
    const token = await this.authService.getValidToken();
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return await firstValueFrom(
      this.httpClient.delete(`${this.apiBase}/admin/dealers/${dealerId}`, { headers })
    );
  }

  /**
   * Admin: Update placeholder dealer metadata (e.g. email, verified status, phone, website).
   */
  public async adminUpdateDealer(dealerId: string, updates: any): Promise<any> {
    const token = await this.authService.getValidToken();
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return await firstValueFrom(
      this.httpClient.patch(`${this.apiBase}/admin/dealers/${dealerId}`, updates, { headers })
    );
  }

  /**
   * Admin: Toggle dealer showroom visibility in public marketplace.
   */
  public async adminToggleDealerShowcase(dealerId: string, showcaseInMarketplace: boolean): Promise<any> {
    return await this.adminUpdateDealer(dealerId, { showcaseInMarketplace });
  }

  /**
   * Public: Retrieve dealer metadata and catalog preview using a VIP claim token.
   */
  public getClaimPreview(claimToken: string): Observable<any> {
    return this.httpClient.get(`${this.apiBase}/claim-gallery/preview?token=${encodeURIComponent(claimToken)}`);
  }

  /**
   * Authenticated: Claim ownership of a pre-seeded showroom and publish its catalog.
   */
  public async claimGallery(claimToken: string): Promise<any> {
    const token = await this.authService.getValidToken();
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return await firstValueFrom(
      this.httpClient.post(`${this.apiBase}/claim-gallery`, { claimToken }, { headers })
    );
  }

  /**
   * Admin: Get executive platform analytics (GMV, profit, user counts, inventory values, support case counts).
   */
  public async adminGetAnalytics(): Promise<any> {
    const token = await this.authService.getValidToken();
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return await firstValueFrom(
      this.httpClient.get(`${this.apiBase}/admin/analytics`, { headers })
    );
  }

  /**
   * Admin: List all platform sales & order transactions with buyer/seller and profit commission breakdown.
   */
  public async adminGetSales(): Promise<any[]> {
    const token = await this.authService.getValidToken();
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res: any = await firstValueFrom(
      this.httpClient.get(`${this.apiBase}/admin/sales`, { headers })
    );
    return res?.sales || [];
  }

  /**
   * Admin: List all support cases and collector inquiries.
   */
  public async adminGetSupportCases(): Promise<any[]> {
    const token = await this.authService.getValidToken();
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res: any = await firstValueFrom(
      this.httpClient.get(`${this.apiBase}/admin/support-cases`, { headers })
    );
    return res?.cases || [];
  }

  /**
   * Admin: Create a new support ticket.
   */
  public async adminCreateSupportCase(caseData: any): Promise<any> {
    const token = await this.authService.getValidToken();
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return await firstValueFrom(
      this.httpClient.post(`${this.apiBase}/admin/support-cases`, caseData, { headers })
    );
  }

  /**
   * Admin: Update support case status, priority, resolution notes, or append an admin message reply.
   */
  public async adminUpdateSupportCase(caseId: string, updates: any): Promise<any> {
    const token = await this.authService.getValidToken();
    const headers: any = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return await firstValueFrom(
      this.httpClient.patch(`${this.apiBase}/admin/support-cases/${caseId}`, updates, { headers })
    );
  }
}

